import { useState, useCallback, useEffect } from 'react';

export const useHistory = (
  existingSeats = [],
  onSeatsCreate,
  existingZones = [],
  onZonesUpdate,
) => {
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Initialize history on mount
  useEffect(() => {
    const initialHistory = [
      {
        id: Date.now(),
        action: 'Başlangıç',
        seats: JSON.parse(JSON.stringify(existingSeats || [])),
        zones: JSON.parse(JSON.stringify(existingZones || [])),
        timestamp: new Date(),
      },
    ];
    setHistory(initialHistory);
    setHistoryIndex(0);
  }, []); // Only on mount

  const saveToHistory = useCallback(
    (newSeats, actionName = 'İşlem', newZones = null) => {
      // Get current zones from the latest history if not provided
      const currentZones = newZones !== null ? newZones : history[historyIndex]?.zones || [];

      const newHistoryItem = {
        id: Date.now(),
        action: actionName,
        seats: JSON.parse(JSON.stringify(newSeats || [])),
        zones: JSON.parse(JSON.stringify(currentZones || [])),
        timestamp: new Date(),
      };

      setHistory((prev) => {
        // Remove any history after current index
        const currentIndex = historyIndex;
        const newHistory = prev.slice(0, currentIndex + 1);
        newHistory.push(newHistoryItem);

        // Keep only last 50 actions for performance
        if (newHistory.length > 50) {
          const trimmed = newHistory.slice(-50);
          // Adjust index for trimmed history
          setHistoryIndex(trimmed.length - 1);
          return trimmed;
        }

        // Set new index for normal case
        setHistoryIndex(newHistory.length - 1);
        return newHistory;
      });
    },
    [historyIndex],
  );

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      const previousSeats = previousState?.seats || [];
      const previousZones = previousState?.zones || [];
      setHistoryIndex(newIndex);
      onSeatsCreate(previousSeats);
      onZonesUpdate && onZonesUpdate(previousZones);
      return { seats: previousSeats, zones: previousZones };
    }
    return null;
  }, [historyIndex, history, onSeatsCreate, onZonesUpdate]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      const nextSeats = nextState?.seats || [];
      const nextZones = nextState?.zones || [];
      setHistoryIndex(newIndex);
      onSeatsCreate(nextSeats);
      onZonesUpdate && onZonesUpdate(nextZones);
      return { seats: nextSeats, zones: nextZones };
    }
    return null;
  }, [historyIndex, history, onSeatsCreate, onZonesUpdate]);

  const goToHistoryState = useCallback(
    (index) => {
      if (index >= 0 && index < history.length) {
        const targetState = history[index];
        const targetSeats = targetState?.seats || [];
        const targetZones = targetState?.zones || [];
        setHistoryIndex(index);
        onSeatsCreate(targetSeats);
        onZonesUpdate && onZonesUpdate(targetZones);
        return { seats: targetSeats, zones: targetZones };
      }
      return null;
    },
    [history, onSeatsCreate, onZonesUpdate],
  );

  return {
    history,
    historyIndex,
    saveToHistory,
    undo,
    redo,
    goToHistoryState,
  };
};
